// ABOUTME: Renders the Kanban modal for moving an applicant between job stages.
// ABOUTME: Collects stage placement, tags, and disqualification details for the move.

import React from "react";
import Button from "@material-ui/core/Button";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import Loading from "customHooks/useCompleoReactHookForm/helpers/Loading";
import useGetMetadata from "customHooks/useGetMetadata";
import { useTranslation } from "react-i18next";
import useValuesFromSource, {
    getManualDisqualificationDefault
} from "customHooks/useValuesFromSource";
import useCompleoRHF from "customHooks/useCompleoReactHookForm";
import { ApiStateType } from "customHooks/useApi";
import FormModal from "customHooks/useCompleoReactHookForm/components/FormModal";
import {
    IJobViewStages,
    IStagesOnlyTotal
} from "Pages/Job/JobView/useJobContext/JobViewTypes";
import { useAuthState } from "_ReactContext/AuthContext";
import { getTotalStage } from "../../helper";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import { useQueryClient } from "react-query";
import * as Sentry from "@sentry/react";
import {
    executeApplicantStageChange,
    getApplicantStageChangeMessageKey,
    submitApplicantStageChange
} from "Pages/Job/applicantStageChange";
import { normalizeApplicantId } from "../../applicantStageReadConsistency";

type actionsType = "disqualify" | "requalify" | "changeStage";

const getStages = (
    allStages: IJobViewStages[],
    stageData: IJobViewStages,
    action: actionsType
) => {
    let stages = allStages;
    switch (action) {
        case "disqualify":
            stages = allStages.filter((i) => i.type === "rejected");
            break;
        case "requalify":
            stages = allStages.filter((i) => i.type !== "rejected");
            break;
        case "changeStage":
            if (stageData.type === "rejected") {
                stages = allStages.filter((i) => i.type === "rejected");
            } else {
                stages = allStages.filter((i) => i.type !== "rejected");
            }
            break;
        default:
            break;
    }

    const stageDataList = stages
        .map((stage) => {
            return {
                label: stage.name,
                value: stage.id
            };
        })
        .filter((i) => i.value !== stageData?.id);
    return stageDataList;
};

const getFieldsToHide = (action: actionsType) => {
    switch (action) {
        case "requalify":
            return ["disqualifyReason"];
        case "changeStage":
            return ["disqualifyReason"];
        default:
            return [];
    }
};

interface IPropsItemFilter {
    open: boolean;
    onClose: (value?: string) => void;
    allStages: IJobViewStages[];
    stageData: IJobViewStages;
    defaultNewStage?: IJobViewStages | null;
    t: any;
    ApplicantData: Compleo.IObject;
    currentIndex: number;
    stagesOnlyTotal: IStagesOnlyTotal[];
    outsideKanban?: boolean;
    listKeyToInvalidate?: string[];
    changeStage?: boolean;
    fnBeforeUpdate?: (
        applicantId: string
    ) =>
        | import("../../applicantStageMutationGeneration").ApplicantStageChange
        | null;
    fnAfterUpdate?: any;
}

const JobKanbanApplicantChangeStageModal = (props: IPropsItemFilter) => {
    const {
        onClose,
        open,
        allStages,
        stageData,
        t: tJobView,
        defaultNewStage = null,
        ApplicantData,
        currentIndex,
        stagesOnlyTotal,
        outsideKanban = false,
        listKeyToInvalidate = [],
        changeStage = false,
        fnBeforeUpdate,
        fnAfterUpdate
    } = props;

    const showMessage = useShowMessage();
    const queryClient = useQueryClient();

    const { company } = useAuthState();
    const action: actionsType = changeStage
        ? "changeStage"
        : stageData?.type !== "rejected"
        ? "disqualify"
        : "requalify";

    const module = "ELASTICLISTSETTINGSBULKADDTOSTAGE";
    const [metadata] = useGetMetadata(module);

    const [t, i18n, readyTranslation] = useTranslation(module, {
        useSuspense: false
    });
    const language = i18n.languages[0];

    const [initialValues] = useValuesFromSource(metadata, false, {
        newStagePosition: { value: "end", label: t("endStageOption") },
        disqualifyReason: getManualDisqualificationDefault(metadata, language),
        tags: ApplicantData?.tags || [],
        manualDisqualification: action === "disqualify"
    });
    const ready = readyTranslation && metadata.status === "success";
    const stages = getStages(allStages, stageData, action);
    const fieldsToHide = getFieldsToHide(action);
    const customList: Compleo.ListDefinitionType[] = [];
    if (readyTranslation) {
        const listPositionData = [
            { label: t("beginStageOption"), value: "begin" },
            { label: t("endStageOption"), value: "end" }
        ];
        customList.push({
            fieldName: "newStagePosition",
            data: listPositionData,
            valueName: "value",
            labelName: "label"
        });
    }
    if (stages.length) {
        customList.push({
            fieldName: "stage",
            data: stages,
            valueName: "value",
            labelName: "label"
        });
        if (defaultNewStage !== null) {
            const stageValue = stages.filter(
                (stage) => stage.value === defaultNewStage.id
            )[0];
            initialValues.stage = stageValue;
        } else {
            initialValues.stage = stages[0];
        }
    }

    const handleSubmit = async (values: any) => {
        const retorno: Compleo.IObject = {};
        retorno.status = 200;

        const applicantId = normalizeApplicantId(ApplicantData.pk);
        const addToEndOfList = values.newStagePosition?.value === "end";
        const newStageId = values.stage?.value;
        const dataPost: Compleo.IObject = {
            companyId: company.companyId,
            jobId: ApplicantData.jobData.JobId,
            applicantId: applicantId,
            stageId: newStageId,
            addToEndOfList: addToEndOfList,
            oldStageId: stageData?.id,
            priorityObj: { orderData: [] },
            newIndex: 0,
            tags: values?.tags
        };
        if (values?.comment) {
            dataPost.comment = values.comment;
        }

        if (action === "disqualify") {
            dataPost.disqualifyReason = values.disqualifyReason;
            dataPost.disqualifyApplicants = true;
        }
        if (action === "requalify") {
            dataPost.requalify = true;
        }
        const stageChangeShouldApply = fnBeforeUpdate?.(ApplicantData.pk);
        if (fnBeforeUpdate && !stageChangeShouldApply) {
            showMessage({
                message: t("ApplicantStageChangeInProgress"),
                snackBarOrign: { vertical: "top", horizontal: "right" },
                timeout: 1000 * 5,
                typeMessage: "warning"
            });
            return { status: 409 };
        }
        const shouldApply = stageChangeShouldApply || (() => true);
        try {
            return await executeApplicantStageChange({
                submit: () => submitApplicantStageChange(dataPost),
                rollback: () => shouldApply(false),
                onPersistenceError: () => {
                    showMessage({
                        message: t("NotIdentifiedProblem"),
                        snackBarOrign: {
                            vertical: "top",
                            horizontal: "right"
                        },
                        timeout: 1000 * 5,
                        typeMessage: "error"
                    });
                },
                onClose,
                synchronize: async (dataAPI) => {
                    if (fnAfterUpdate) {
                        const destinationIndex = addToEndOfList
                            ? getTotalStage(stagesOnlyTotal, [newStageId])
                            : 0;
                        await fnAfterUpdate({
                            outsideKanban,
                            destinationIndex,
                            applicantId: ApplicantData.pk,
                            stageData,
                            currentIndex,
                            newStageId,
                            requestId: dataAPI.data?.requestId,
                            stageChangeShouldApply: shouldApply
                        });
                    }
                },
                listKeyToInvalidate,
                invalidateQuery: (keyName) =>
                    queryClient.invalidateQueries(keyName),
                onPersisted: (dataAPI) => {
                    const messageKey = getApplicantStageChangeMessageKey(
                        dataAPI
                    );
                    if (messageKey) {
                        showMessage({
                            message: t(messageKey),
                            snackBarOrign: {
                                vertical: "top",
                                horizontal: "right"
                            },
                            timeout: 1000 * 5,
                            typeMessage: "warning"
                        });
                    }
                },
                onCompletionError: (error, context) => {
                    Sentry.captureException(error, {
                        tags: {
                            applicant_stage_change_operation: context.operation
                        },
                        extra: context.queryKey
                            ? { queryKey: context.queryKey }
                            : undefined
                    });
                },
                successResponse: retorno
            });
        } finally {
            stageChangeShouldApply?.finish();
        }
    };

    const postReturn: ApiStateType = {
        status: "success",
        response: {},
        exception: null
    };
    const title = changeStage
        ? tJobView("ModalChangeStage_title")
        : action === "disqualify"
        ? tJobView("ModalDisqualify_title")
        : tJobView("ModalRequalify_title");

    const [formCompleo, finished, handleSubmitRHF] = useCompleoRHF({
        t: t,
        ready: ready,
        i18nV: i18n,
        postMethod: handleSubmit,
        postReturn: postReturn,
        FormType: FormModal,
        metadadosRetorno: metadata,
        valuesFromSource: initialValues,
        formGroupPaperElevation: 0,
        customLists: customList,
        fieldsToHide: fieldsToHide
    });

    const handleSave = async () => {
        await handleSubmitRHF();
    };

    let FormReturn: JSX.Element = <Loading />;
    if (finished && ready) {
        FormReturn = formCompleo;
    }

    return (
        <Dialog
            fullWidth
            disableBackdropClick
            disableEscapeKeyDown
            maxWidth="lg"
            aria-labelledby={title}
            open={open}
        >
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>{FormReturn}</DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()} color="secondary">
                    {t("COMPLEOGENERAL_CLOSE")}
                </Button>
                <Button
                    onClick={() => handleSave()}
                    variant="contained"
                    color="primary"
                >
                    {t("COMPLEOGENERAL_SAVE")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobKanbanApplicantChangeStageModal;
